import React, { useState, useEffect } from "react";
import api from "../../http";
import MaskPhoneNumber from "../../Utils/MaskPhoneNumber";
import Validator, {
  ValidationTypes as V_Type,
} from "react-form-supervalidator";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { addPatient } from "../../Redux/TabsSlice/TabsSlice";
import FullPageLoader from "../../Components/FullPageLoader/FullPageLoader";
import { ErrorHandlingMessage } from "Utils/ErrorHandlingMessage";
import UseZipCode from "CustomHooks/UseZipCode";
import FetchZipCode from "Services/FetchZipCode";
import CustomDraggable from "Common/CustomDraggable/CustomDraggable";
import ReactPortal from "Components/ReactPortal/ReactPortal";
import { SsnValidator, InvalidDateValidator } from "Utils/validators";
import PopupHeader from "Components/PopupHeader/PopupHeader";
import CustomButtonNew from "Common/CustomButtonNew/CustomButtonNew";
import PlusIcon from "SVGS/PlusIcon";
import PropTypes from "prop-types";
import { RECORD_CREATED_SUCCESSFULLY } from "Common/ToastConstants";
import { MAX_FILE_SIZE_30MB } from "Common/Constants";
import { motion } from "framer-motion";
import { Tooltip } from "react-tooltip";
import BasicInformation from "./sections/BasicInformation/BasicInformation";
import InsuranceInformation from "./sections/InsuranceInformation/InsuranceInformation";
import SuccessScreen from "./sections/SuccessScreen";
import EligibiltyPopup from "../EligibiltyPopup/EligibiltyPopup";
import ConfirmationPopup from "../ConfirmationPopup/ConfirmationPopup";
import PatientEligibilityPopup from "../../Modules/Insurance/PatientInsurance/PatientInsurancePopups/PatientEligibilityPopup";
 
const getDefaultDocumentTemplate = (name) => ({
  addedBy: "",
  addedDate: "",
  updatedBy: "",
  updatedDate: "",
  inActive: false,
  inActiveReason: "",
  inActiveBy: "",
  inActiveDate: "",
  id: 0,
  name,
  documentTypeId: null,
  patientId: 0,
  patient: "",
  originalPatientId: 0,
  size: "",
  url: "",
  notes: "",
  moduleName: "patientplan",
  moduleRefId: 0,
  preSignedUrl: "",
});

const getDefaultPlanModel = () => ({
  addedBy: "",
  addedDate: "",
  updatedBy: "",
  updatedDate: "",
  inActive: false,
  inActiveReason: "",
  inActiveBy: "",
  inActiveDate: "",
  id: 0,
  patientId: 0,
  patient: "",
  firstName: "",
  middleInitial: "",
  lastName: "",
  gender: "",
  insurancePlanID: 0,
  insurancePayerName: "",
  coverage: "",
  relationShip: "",
  address1: "",
  city: "",
  state: "",
  ssn: "",
  zipCode: "",
  subscriberId: "",
  groupName: "",
  groupNumber: "",
  copay: 0,
  deductible: 0,
  coInsurance: 0,
  outOfPocketMax: 0,
  eligibilityStatus: "",
  email: "",
  dob: "",
  patientDocument: [], // Each plan has its own document array
  subscriberSameAsPatientAddress: true,
});

const getInitialPatientModel = () => ({
  practiceId: 0,
  addedBy: "",
  addedDate: "",
  updatedBy: "",
  updatedDate: "",
  inActive: false,
  inActiveReason: "",
  inActiveBy: "",
  inActiveDate: "",
  id: 0,
  accountNum: "",
  firstName: "",
  middleInitial: "",
  lastName: "",
  title: "",
  ssn: "",
  dob: "",
  gender: "",
  maritalStatus: "",
  GuarantorRelation: "18",
  phoneNumber: "",
  email: "",
  address1: "",
  city: "",
  state: "",
  zipCode: "",
  status: "Active",
  externalPatientId: "",
  isSelfPay: false,
  // Store all insurance plans as an array of objects under patientPlan
  // Index 0 = Primary, 1 = Secondary, 2 = Tertiary
  patientPlan: [
    {
      ...getDefaultPlanModel(),
      coverage: "P", // Default to Primary
    },
  ],
  patientGuarantor: {
    addedBy: "",
    addedDate: "",
    updatedBy: "",
    updatedDate: "",
    inActive: false,
    inActiveReason: "",
    inActiveBy: "",
    inActiveDate: "",
    id: 0,
    patient: "",
    firstName: "",
    middleName: "",
    lastName: "",
    ssn: "",
    gender: "",
    dob: "",
    relationship: "",
    status: "T",
    endDateEffective: "",
    employeeId: "",
    employeeStatus: "",
    address1: "",
    address2: "",
    city: "",
    state: "",
    zipCode: "",
    mobileNumber: "",
    phoneNumber: "",
    phoneNumberExt: "",
    email: "",
    fax: "",
    savedFromExternal: false,
    originalPatientId: 0,
    externalPatientGuarantorId: "",
  },
  doOverrideDuplicateEmail: false,
  doOverrideDuplicatePhoneNumber: false,
  doOverrideDuplicateAddress: false,
  communicationEmail: false,
  communicationText: false,
  communicationMail: false,
});

const getInitialValidationModel = () => ({
  zipCodeError: null,
  dobError: null,
  firstName: null,
  lastName: null,
  genderError: null,
  address1Error: null,
  emailError: null,
  phoneNumberError: null,
  // Guarantor validation
  guarantorFirstNameError: null,
  guarantorLastNameError: null,
  guarantorDobError: null,
  guarantorGenderError: null,
  // Insurance validation
  insurancePayerError: null,
  insuranceCoverageError: null,
  insuranceSubscriberIdError: null,
  insuranceRelationShipError: null,
  planBeginDateError: null,
  coverageStartEndDateError: null,
  // Subscriber validation
  subscriberFirstNameError: null,
  subscriberLastNameError: null,
  subscriberDobError: null,
  subscriberGenderError: null,
  subscriberAddress1Error: null,
  subscriberZipCodeError: null,
});

const getInitialInsuranceValidationModel = () => ({
  insurancePayerError: null,
  insuranceSubscriberIdError: null,
  insuranceRelationShipError: null,
  planBeginDateError: null,
  coverageStartEndDateError: null,
  subscriberFirstNameError: null,
  subscriberLastNameError: null,
  subscriberDobError: null,
  subscriberGenderError: null,
  subscriberAddress1Error: null,
  subscriberZipCodeError: null,
});

const NewPatientPopup = ({ onClose, disableDraggable }) => {
  const UNIQUE_ID = "NewPatientPopup";
  const [ptModel, setPTModel] = useState(getInitialPatientModel());
  const [validationModel, setValidationModel] = useState(getInitialValidationModel());
  const [validationByPlan, setValidationByPlan] = useState({});
  const [loading, setLoading] = useState(false);
  const [showOnlyRequired, setShowOnlyRequired] = useState(false);
  const [sectionsOpen, setSectionsOpen] = useState({
    identification: true,
    address: true,
    contact: true,
    guarantor: false,
  });
  const [insuranceExpanded, setInsuranceExpanded] = useState(true);
  const [insuranceSectionsOpen, setInsuranceSectionsOpen] = useState({
    cards: true,
    plan: true,
    group: true,
    costShare: true,
    subscriber: true,
  });
  const [insuranceCardFront, setInsuranceCardFront] = useState(null);
  const [insuranceCardBack, setInsuranceCardBack] = useState(null);
  const [insurancePlans, setInsurancePlans] = useState([
    { coverage: "P", expanded: true, sectionsOpen: {
      cards: true,
      plan: true,
      group: true,
      costShare: true,
      subscriber: true,
    }, cardFront: null, cardBack: null }
  ]); // Array to track multiple insurance plans
  const [showSuccessScreen, setShowSuccessScreen] = useState(false);
  const [showEligibilityPopup, setShowEligibilityPopup] = useState(false);
  const [showPatientEligibilityPopup, setShowPatientEligibilityPopup] = useState(false);
  const [pendingPatientToOpen, setPendingPatientToOpen] = useState(null);
  const [initialEligibilityResult, setInitialEligibilityResult] = useState(null);
  const [eligibilityData, setEligibilityData] = useState({
    patientId: null,
    patientName: "",
    payerId: null,
    subscriberId: "",
    patientDob: "",
    dateOfService: "",
    cptCode: "",
    serviceType: "",
    patientPlanId: null,
    subscriberFirstName: "",
    subscriberLastName: "",
    patientRelation: "",
    insurancePayerTypeId: null,
  });
  const [duplicateEmailConfirmation, setDuplicateEmailConfirmation] = useState(false);
  const [duplicatePhoneConfirmation, setDuplicatePhoneConfirmation] = useState(false);
  const [duplicateAddressConfirmation, setDuplicateAddressConfirmation] = useState(false);
  const [pendingCheckEligibility, setPendingCheckEligibility] = useState(false);

  const dispatch = useDispatch();
  const { onPasteZip } = UseZipCode(
    setPTModel,
    setValidationModel,
    ptModel.zipCode
  );

  const confirmations = [
    {
      condition: duplicateEmailConfirmation,
      onCancel: () => setDuplicateEmailConfirmation(false),
      message: (
        <>
          A patient with this email <b>{ptModel.email}</b> already exists.
          <p>Are you sure you want to continue?</p>
        </>
      ),
    },
    {
      condition: duplicateAddressConfirmation,
      onCancel: () => setDuplicateAddressConfirmation(false),
      message: (
        <>
          A patient with this address already exists.{" "}
          <b>
            {ptModel.address1}, {ptModel.city} {ptModel.state}, {ptModel.zipCode}
          </b>
          <p>Are you sure you want to continue?</p>
        </>
      ),
    },
    {
      condition: duplicatePhoneConfirmation,
      onCancel: () => setDuplicatePhoneConfirmation(false),
      message: (
        <>
          A patient with this <b>Phone number: {MaskPhoneNumber(ptModel.phoneNumber ?? '')}</b> already exists.
          <p>Are you sure you want to continue?</p>
        </>
      ),
    },
  ];

  const toggleSection = (section) => {
    setSectionsOpen((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const toggleInsuranceSection = (section) => {
    setInsuranceSectionsOpen((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const handleCardUpload = (e, cardType, planIndex = 0) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedFileTypes = [
      "image/jpeg",
      "image/jpg",
      "image/png",
      "image/gif",
      "application/pdf"
    ];

    if (!allowedFileTypes.includes(file.type)) {
      toast.warning("Please upload a valid file (JPG, JPEG, PNG, GIF, or PDF)");
      e.target.value = "";
      return;
    }

    if (file.size > MAX_FILE_SIZE_30MB) {
      toast.warning("File size exceeds 30MB limit");
      e.target.value = "";
      return;
    }

    if (planIndex === 0) {
      // Primary plan uses the existing state
      if (cardType === "front") {
        setInsuranceCardFront(file);
      } else {
        setInsuranceCardBack(file);
      }
    } else {
      // Additional plans use the insurancePlans state
      setInsurancePlans((prev) => {
        const updated = [...prev];
        if (updated[planIndex]) {
          updated[planIndex] = {
            ...updated[planIndex],
            [cardType === "front" ? "cardFront" : "cardBack"]: file,
          };
        }
        return updated;
      });
    }
  };

  const handleCardRemove = (cardType, planIndex = 0) => {
    if (planIndex === 0) {
      if (cardType === "front") {
        setInsuranceCardFront(null);
      } else {
        setInsuranceCardBack(null);
      }
    } else {
      setInsurancePlans((prev) => {
        const updated = [...prev];
        if (updated[planIndex]) {
          updated[planIndex] = {
            ...updated[planIndex],
            [cardType === "front" ? "cardFront" : "cardBack"]: null,
          };
        }
        return updated;
      });
    }
  };

  const handleAddInsurancePlan = () => {
    if (insurancePlans.length >= 4) {
      toast.warning("Maximum of 4 insurance plans allowed (Primary, Secondary, Tertiary, Other)");
      return;
    }

    const usedCoverages = (ptModel.patientPlan || []).map((p) => p?.coverage).filter(Boolean);
    const allCoverageTypes = ["P", "S", "T", "O"];
    const newCoverage = allCoverageTypes.find((c) => !usedCoverages.includes(c)) || "";

    setInsurancePlans((prev) => [
      ...prev,
      {
        coverage: newCoverage,
        expanded: true,
        sectionsOpen: {
          cards: true,
          plan: true,
          group: true,
          costShare: true,
          subscriber: true,
        },
        cardFront: null,
        cardBack: null,
      },
    ]);

    // Add new plan to ptModel.patientPlan array
    setPTModel((prev) => ({
      ...prev,
      patientPlan: [
        ...(prev.patientPlan || []),
        {
          ...getDefaultPlanModel(),
          coverage: newCoverage,
        },
      ],
    }));
  };

  const handleRemoveInsurancePlan = (planIndex) => {
    if (planIndex === 0) {
      toast.warning("Cannot remove the primary insurance plan");
      return;
    }

    setInsurancePlans((prev) => prev.filter((_, index) => index !== planIndex));
    setPTModel((prev) => ({
      ...prev,
      patientPlan: prev.patientPlan.filter((_, index) => index !== planIndex),
    }));
  };

  const handlePlanChange = (e, planIndex) => {
    // All plans share the same handler now; delegate to handleChange with proper nested path
    handleChange(e);
  };

  const handleToggleInsuranceSection = (section, planIndex = 0) => {
    if (planIndex === 0) {
      toggleInsuranceSection(section);
    } else {
      setInsurancePlans((prev) => {
        const updated = [...prev];
        if (updated[planIndex]) {
          updated[planIndex] = {
            ...updated[planIndex],
            sectionsOpen: {
              ...updated[planIndex].sectionsOpen,
              [section]: !updated[planIndex].sectionsOpen[section],
            },
          };
        }
        return updated;
      });
    }
  };

  const handleInsuranceExpandedChange = (planIndex = 0) => {
    if (planIndex === 0) {
      handleToggleInsuranceExpanded();
    } else {
      setInsurancePlans((prev) => {
        const updated = [...prev];
        if (updated[planIndex]) {
          updated[planIndex] = {
            ...updated[planIndex],
            expanded: !updated[planIndex].expanded,
          };
        }
        return updated;
      });
    }
  };

  const buildValidationForPlan = (planData, ptModel = {}) => {
    const showSubscriber =
      planData?.relationShip && planData?.relationShip !== "18";
    const isSameAsPatient = planData?.subscriberSameAsPatientAddress ?? false;
    const effectiveAddress1 = isSameAsPatient ? (ptModel?.address1 ?? "") : (planData?.address1 ?? "");
    const effectiveZipCode = isSameAsPatient ? (ptModel?.zipCode ?? "") : (planData?.zipCode ?? "");
    return {
      ...getInitialInsuranceValidationModel(),
      insurancePayerError: (() => {
        const hasPayer =
          planData?.insurancePlanID > 0 &&
          planData?.insurancePayerName?.trim() !== "";
        return Validator(
          hasPayer ? planData.insurancePayerName : "",
          [V_Type.required],
          ["Payer is required"]
        );
      })(),
      insuranceSubscriberIdError: Validator(
        planData?.subscriberId ?? "",
        [V_Type.required],
        ["Subscriber ID is required"]
      ),
      insuranceRelationShipError: Validator(
        planData?.relationShip ?? "",
        [V_Type.required],
        ["Relationship to Insured is required"]
      ),
      planBeginDateError: Validator(
        planData?.planBeginDate,
        [V_Type.required, InvalidDateValidator],
        [
          "Coverage date is required",
          "Coverage date from can not be future date",
        ]
      ),
      coverageStartEndDateError: Validator(
        [planData?.planBeginDate, planData?.planEndDate],
        V_Type.startEndDate,
        "Start date should be less then End date"
      ),
      ...(showSubscriber && {
        subscriberFirstNameError: Validator(
          planData?.firstName ?? "",
          [V_Type.required],
          ["First Name is required"]
        ),
        subscriberLastNameError: Validator(
          planData?.lastName ?? "",
          [V_Type.required],
          ["Last Name is required"]
        ),
        subscriberDobError: Validator(
          planData?.dob,
          [V_Type.required, InvalidDateValidator],
          [
            "DOB is required",
            "Date must be in between 01/02/1900 or current Date",
          ]
        ),
        subscriberGenderError: Validator(
          planData?.gender ?? "",
          [V_Type.required],
          ["Gender is required"]
        ),
        subscriberAddress1Error: Validator(
          effectiveAddress1,
          [V_Type.required],
          ["Street Address is required"]
        ),
        subscriberZipCodeError: Validator(
          effectiveZipCode,
          [V_Type.required],
          ["Zip Code is required"]
        ),
      }),
    };
  };

  const setValidation = () => {
    const basicValidationObj = {
      ...validationModel,
      dobError: Validator(
        ptModel.dob,
        [V_Type.required, InvalidDateValidator],
        [
          "DOB is required",
          "Date must be in between 01/02/1900 or current Date",
        ]
      ),
      firstName: Validator(
        ptModel.firstName,
        [V_Type.required],
        ["First Name is required"]
      ),
      lastName: Validator(
        ptModel.lastName,
        [V_Type.required],
        ["Last Name is required"]
      ),
      genderError: Validator(
        ptModel.gender,
        [V_Type.required],
        ["Gender is required"]
      ),
      address1Error: null,
      zipCodeError: null,
      emailError: null,
      phoneNumberError: null,
      ssnError: Validator(
        ptModel.ssn,
        [SsnValidator],
        ["SSN length must be equal to 9 digits."]
      ),
      ...(ptModel.GuarantorRelation && ptModel.GuarantorRelation !== "18" && {
        guarantorFirstNameError: Validator(
          ptModel.patientGuarantor.firstName,
          [V_Type.required],
          ["First Name is required"]
        ),
        guarantorLastNameError: Validator(
          ptModel.patientGuarantor.lastName,
          [V_Type.required],
          ["Last Name is required"]
        ),
        guarantorDobError: Validator(
          ptModel.patientGuarantor.dob,
          [V_Type.required],
          ["Date of Birth is required"]
        ),
        guarantorGenderError: Validator(
          ptModel.patientGuarantor.gender,
          [V_Type.required],
          ["Gender is required"]
        ),
      }),
      ...(hasInsuranceDataStarted() && {
        address1Error: Validator(
          ptModel.address1,
          [V_Type.required],
          ["Street Address is required"]
        ),
        zipCodeError: Validator(
          ptModel.zipCode,
          [V_Type.required],
          ["Zip Code is required"]
        ),
      }),
    };

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (ptModel.email && !emailRegex.test(ptModel.email)) {
      basicValidationObj.emailError = (
        <div className="text-red-500">
          Please enter a valid email address
        </div>
      );
    } else {
      basicValidationObj.emailError = null;
    }

    const phoneDigits = ptModel.phoneNumber ? ptModel.phoneNumber.replace(/\D/g, '') : '';
    if (ptModel.phoneNumber && phoneDigits.length !== 10) {
      basicValidationObj.phoneNumberError = (
        <div className="text-red-500">
          Please enter a valid phone number
        </div>
      );
    } else {
      basicValidationObj.phoneNumberError = null;
    }

    setValidationModel(basicValidationObj);
    const basicHasErrors = Validator(basicValidationObj, V_Type.NullCheck);

    let planHasErrors = false;
    const nextValidationByPlan = {};
    if (hasInsuranceDataStarted()) {
      const plans = ptModel.patientPlan || [];
      plans.forEach((plan, index) => {
        const planData = plan || {};
        const planValidationObj = buildValidationForPlan(planData, ptModel);
        nextValidationByPlan[index] = planValidationObj;
        if (Validator(planValidationObj, V_Type.NullCheck)) {
          planHasErrors = true;
        }
      });
      setValidationByPlan(nextValidationByPlan);
    } else {
      setValidationByPlan({});
    }

    const hasAddressValidationErrors = Boolean(
      basicValidationObj.address1Error || basicValidationObj.zipCodeError
    );
    if (hasInsuranceDataStarted() && hasAddressValidationErrors) {
      setSectionsOpen((prev) =>
        prev.address ? prev : { ...prev, address: true }
      );
    }

    return basicHasErrors || planHasErrors;
  };

  const handleChange = (e) => {
    let { name, value, dataset } = e.target;
    const nestedPath = dataset?.nested;
    
     
    if (name === "phoneNumber" || name === "mobileNumber" || 
        (nestedPath && (nestedPath.includes("phoneNumber") || nestedPath.includes("mobileNumber")))) {
      value = MaskPhoneNumber(value, "removeMask");
    }
    
     
    if (nestedPath) {
      // Check if it's for patientPlan array (patientPlan.<index>.<field>)
      if (nestedPath.startsWith("patientPlan.")) {
        const pathParts = nestedPath.split('.');
        const planIndex = parseInt(pathParts[1]); // Get the index from patientPlan.0.field
        const remainingPath = pathParts.slice(2).join('.'); // Get the field path
        
        setPTModel((prev) => {
          const newModel = { ...prev };
          if (!newModel.patientPlan) {
            newModel.patientPlan = [];
          }
          if (!newModel.patientPlan[planIndex]) {
            newModel.patientPlan[planIndex] = { ...getDefaultPlanModel() };
          }
          
          const plan = newModel.patientPlan[planIndex];
          if (remainingPath) {
            const fieldParts = remainingPath.split('.');
            let current = plan;
            for (let i = 0; i < fieldParts.length - 1; i++) {
              if (!current[fieldParts[i]]) {
                current[fieldParts[i]] = {};
              }
              current = current[fieldParts[i]];
            }
            const finalKey = fieldParts[fieldParts.length - 1];
            current[finalKey] = value;
          } else {
            plan[name] = value;
          }
          
          if (name === "relationShip" && value === "18") {
            plan.firstName = "";
            plan.middleInitial = "";
            plan.lastName = "";
            plan.gender = "";
            plan.address1 = "";
            plan.city = "";
            plan.state = "";
            plan.zipCode = "";
            plan.email = "";
            plan.dob = "";
            plan.subscriberSameAsPatientAddress = false;
          } else if (name === "relationShip" && value !== "18") {
            plan.subscriberSameAsPatientAddress = true;
            plan.address1 = newModel.address1 ?? "";
            plan.city = newModel.city ?? "";
            plan.state = newModel.state ?? "";
            plan.zipCode = newModel.zipCode ?? "";
          }
          
          return newModel;
        });
        
        // Update insurancePlans state for subscriber section toggle
        if (name === "relationShip") {
          setInsurancePlans((prev) => {
            const updated = [...prev];
            if (updated[planIndex + 1]) {
              updated[planIndex + 1] = {
                ...updated[planIndex + 1],
                sectionsOpen: {
                  ...updated[planIndex + 1].sectionsOpen,
                  subscriber: value && value !== "18",
                },
              };
            }
            return updated;
          });
        }
      } else {
        // Generic nested object handling (non-plan fields)
        const pathParts = nestedPath.split('.');
        setPTModel((prev) => {
          const newModel = { ...prev };
          let current = newModel;

          for (let i = 0; i < pathParts.length - 1; i++) {
            if (!current[pathParts[i]]) {
              current[pathParts[i]] = {};
            }
            current = current[pathParts[i]];
          }
          const finalKey = pathParts[pathParts.length - 1];
          current[finalKey] = value;
          return newModel;
        });
      }
      
    } else {
       
      setPTModel((prev) => {
        const updated = { ...prev, [name]: value };
        if (name === "middleName") {
          updated.middleInitial = value ? value.charAt(0).toUpperCase() : "";
        }
        
        if (name === "phoneNumber") {
          const phoneValue = value.replace(/\D/g, ''); 
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          const hasValidEmail = updated.email && emailRegex.test(updated.email);
          updated.communicationEmail = false;
          updated.communicationText = false;
          updated.communicationMail = false;
          if (phoneValue.length >= 10) {
            updated.communicationText = !hasValidEmail;
            updated.communicationEmail = hasValidEmail;
          } else if (phoneValue.length === 0 && hasValidEmail) {
            updated.communicationEmail = true;
          }

          if (value && phoneValue.length !== 10) {
            setValidationModel((prev) => ({
              ...prev,
              phoneNumberError: (
                <div className="text-red-500">
                  Please enter a valid phone number
                </div>
              ),
            }));
          } else {
            setValidationModel((prev) => ({ ...prev, phoneNumberError: null }));
          }
        }
        
        return updated;
      });

      if (name === "GuarantorRelation") {
        setSectionsOpen((prev) => ({
          ...prev,
          guarantor: value && value !== "18",
        }));
      }
    }
  };

  const handleGuarantorZipChange = async (e) => {
    const zip = e.target.value;
    setPTModel((prev) => ({
      ...prev,
      patientGuarantor: {
        ...prev.patientGuarantor,
        zipCode: zip,
      },
    }));
    
    try {
      if (zip.length >= 5 && zip.length <= 9) {
        const zipCode = zip.length > 5 ? zip.substring(0, 5) : zip;
        const response = await FetchZipCode(zipCode);
        setPTModel((prev) => ({
          ...prev,
          patientGuarantor: {
            ...prev.patientGuarantor,
            city: response.data.dataModel.city,
            state: response.data.dataModel.stateId,
          },
        }));
      } else if (zip.length === 0) {
        setPTModel((prev) => ({
          ...prev,
          patientGuarantor: {
            ...prev.patientGuarantor,
            city: "",
            state: "",
          },
        }));
      }
    } catch (error) {
      ErrorHandlingMessage(error);
      setPTModel((prev) => ({
        ...prev,
        patientGuarantor: {
          ...prev.patientGuarantor,
          city: "",
          state: "",
        },
      }));
    }
  };

  const handleSubscriberZipChange = async (e, planIndex = 0) => {
    const zip = e.target.value;

    // Update zip on the selected plan (default: primary = index 0)
    setPTModel((prev) => {
      const plans = [...(prev.patientPlan || [])];
      const index = planIndex;
      if (!plans[index]) {
        plans[index] = { ...getDefaultPlanModel() };
      }
      plans[index] = {
        ...plans[index],
        zipCode: zip,
      };

      return {
        ...prev,
        patientPlan: plans,
      };
    });

    try {
      if (zip.length >= 5 && zip.length <= 9) {
        const zipCode = zip.length > 5 ? zip.substring(0, 5) : zip;
        const response = await FetchZipCode(zipCode);

        setPTModel((prev) => {
          const plans = [...(prev.patientPlan || [])];
          const index = planIndex;
          if (!plans[index]) {
            plans[index] = { ...getDefaultPlanModel() };
          }
          plans[index] = {
            ...plans[index],
            city: response.data.dataModel.city,
            state: response.data.dataModel.stateId,
          };

          return {
            ...prev,
            patientPlan: plans,
          };
        });
      } else if (zip.length === 0) {
        setPTModel((prev) => {
          const plans = [...(prev.patientPlan || [])];
          const index = planIndex;
          if (!plans[index]) {
            plans[index] = { ...getDefaultPlanModel() };
          }
          plans[index] = {
            ...plans[index],
            city: "",
            state: "",
          };

          return {
            ...prev,
            patientPlan: plans,
          };
        });
      }
    } catch (error) {
      ErrorHandlingMessage(error);
      setPTModel((prev) => {
        const plans = [...(prev.patientPlan || [])];
        const index = planIndex;
        if (!plans[index]) {
          plans[index] = { ...getDefaultPlanModel() };
        }
        plans[index] = {
          ...plans[index],
          city: "",
          state: "",
        };

        return {
          ...prev,
          patientPlan: plans,
        };
      });
    }
  };

  const handleSubscriberSameAsPatientAddress = (checked, planIndex = 0) => {
    setPTModel((prev) => {
      const plans = [...(prev.patientPlan || [])];
      if (!plans[planIndex]) {
        plans[planIndex] = { ...getDefaultPlanModel() };
      }
      if (checked) {
        plans[planIndex] = {
          ...plans[planIndex],
          subscriberSameAsPatientAddress: true,
          address1: prev.address1 ?? "",
          city: prev.city ?? "",
          state: prev.state ?? "",
          zipCode: prev.zipCode ?? "",
        };
      } else {
        plans[planIndex] = {
          ...plans[planIndex],
          subscriberSameAsPatientAddress: false,
          address1: "",
          city: "",
          state: "",
          zipCode: "",
        };
      }
      return { ...prev, patientPlan: plans };
    });
  };

  const onPasteEmail = (e) => {
    let email = e.target.value;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const isValidEmail = email && emailRegex.test(email);
    
    setPTModel((prev) => {
      const updated = { ...prev, email: email };
      
      updated.communicationEmail = false;
      updated.communicationText = false;
      updated.communicationMail = false;
      
      if (isValidEmail) {
        updated.communicationEmail = true;
      } else {
        const phoneValue = updated.phoneNumber ? updated.phoneNumber.replace(/\D/g, '') : '';
        if (phoneValue.length >= 10) {
          updated.communicationText = true;
        }
      }
      
      return updated;
    });

    if (email && !emailRegex.test(email)) {
      setValidationModel((prev) => ({
        ...prev,
        emailError: (
          <div className="text-red-500">
            Please enter a valid email address
          </div>
        ),
      }));
    } else {
      setValidationModel((prev) => ({ ...prev, emailError: null }));
    }
  };

  const handleCommunicationPreferenceChange = (updatedModel) => {
    setPTModel(updatedModel);
    setValidationModel((prev) => ({
      ...prev,
      emailError: null,
      phoneNumberError: null,
    }));
  };

  
  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const mimeType = file.type || 'image/jpeg';
        const base64String = reader.result.split(',')[1];
        resolve(`data:${mimeType};base64,${base64String}`);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

 
  const getFileExtension = (file) => {
    const name = file.name || '';
    const extension = name.split('.').pop()?.toLowerCase() || '';
    return extension;
  };

  const hasInsuranceDataStarted = () => {
    const primaryPlan = ptModel.patientPlan?.[0];

    const hasInsuranceCards = !!(insuranceCardFront || insuranceCardBack);

    const hasInsuranceFields =
      primaryPlan &&
      !!(
        (primaryPlan.insurancePlanID && primaryPlan.insurancePlanID > 0) ||
        (primaryPlan.insurancePayerName &&
          primaryPlan.insurancePayerName.trim() !== "") ||
        (primaryPlan.subscriberId && primaryPlan.subscriberId.trim() !== "") ||
        (primaryPlan.relationShip && primaryPlan.relationShip.trim() !== "")
      );

    return hasInsuranceCards || hasInsuranceFields;
  };

 
  const isInsurancePlanValid = () => {
    const plan = ptModel.patientPlan?.[0];
    if (!plan) return false;
    const hasPayerInfo = !!(
      plan.insurancePlanID &&
      plan.insurancePlanID > 0 &&
      plan.insurancePayerName &&
      plan.insurancePayerName.trim() !== ""
    );
    
    const hasPlanInfo = !!(
      plan.coverage &&
      plan.coverage.trim() !== "" &&
      plan.subscriberId &&
      plan.subscriberId.trim() !== "" &&
      plan.relationShip &&
      plan.relationShip.trim() !== ""
    );
    return hasPayerInfo && hasPlanInfo;
  };

  const handleSave = async (checkEligibility = false) => {
    let myValidation = setValidation();
    if (!myValidation) {
      setPendingCheckEligibility(checkEligibility);
      try {
        setLoading(true);
        
        // Build patientPlan array with documents for each plan
        const plansWithDocuments = await Promise.all(
          (ptModel.patientPlan || []).map(async (plan, planIndex) => {
            const planDocuments = [...(plan.patientDocument || [])];
            
            // Get card files for this plan
            let cardFront = null;
            let cardBack = null;
            
            if (planIndex === 0) {
              // Primary plan uses separate state
              cardFront = insuranceCardFront;
              cardBack = insuranceCardBack;
            } else {
              // Additional plans use insurancePlans state
              const planState = insurancePlans[planIndex];
              cardFront = planState?.cardFront || null;
              cardBack = planState?.cardBack || null;
            }
            
            // Convert front card to document
            if (cardFront) {
              const base64String = await fileToBase64(cardFront);
              const extension = getFileExtension(cardFront);
              planDocuments.push({
                ...getDefaultDocumentTemplate("patient_insurancecard_front"),
                uploadedDocument: base64String,
                uploadedDocumentExt: extension,
              });
            }
            
            // Convert back card to document
            if (cardBack) {
              const base64String = await fileToBase64(cardBack);
              const extension = getFileExtension(cardBack);
              planDocuments.push({
                ...getDefaultDocumentTemplate("patient_insurancecard_back"),
                uploadedDocument: base64String,
                uploadedDocumentExt: extension,
              });
            }
            
            return {
              ...plan,
              patientDocument: planDocuments,
            };
          })
        );

        const plansForPayload = plansWithDocuments.map((plan) =>
          plan.subscriberSameAsPatientAddress
            ? {
                ...plan,
                address1: ptModel.address1 ?? "",
                city: ptModel.city ?? "",
                state: ptModel.state ?? "",
                zipCode: ptModel.zipCode ?? "",
              }
            : plan
        );

        const patientGuarantor = ptModel.patientGuarantor
          ? { ...ptModel.patientGuarantor, relationship: ptModel.GuarantorRelation }
          : null;

        const payload = {
          ...ptModel,
          patientPlan: plansForPayload, // Array of plans, each with its own patientDocument array
          patientGuarantor,
          doOverrideDuplicateEmail: duplicateEmailConfirmation,
          doOverrideDuplicateAddress: duplicateAddressConfirmation,
          doOverrideDuplicatePhoneNumber: duplicatePhoneConfirmation,
        };

        const response = await api.post(
          `${process.env.REACT_APP_TC_PMS}/api/Patient/RegisterPatient`,
          payload
        );
        const obj = {
          active: true,
          id: response.data.dataModel.id,
          subTab: "Overview",
          name: `${response.data.dataModel.lastName}, ${response.data.dataModel.firstName}`,
          unsavedChanges: false,
          insuranceData: { status: false, editMode: false },
        };
        
        setLoading(false);
        setDuplicateEmailConfirmation(false);
        setDuplicatePhoneConfirmation(false);
        setDuplicateAddressConfirmation(false);
        const shouldCheckEligibility = checkEligibility || pendingCheckEligibility;
        setPendingCheckEligibility(false);
        toast.success(RECORD_CREATED_SUCCESSFULLY);
       
        const primaryPlan = plansWithDocuments[0];
        
        if (shouldCheckEligibility && primaryPlan?.insurancePlanID) {
          setPendingPatientToOpen(obj); 
          try {
            const patientId = response.data.dataModel.id;
            // API returns patientPlan as array, get first plan's id
            const patientPlanId = response.data.dataModel.patientPlan?.[0]?.id || 0;
            const plansResponse = await api.get(
              `${process.env.REACT_APP_TC_PMS}/api/DropDown/FindPatientPlanByPatientId?PatientId=${patientId}`
            );
            const plans = plansResponse.data.dataModel || [];
            const firstPlan = plans[0];  
            setEligibilityData({
              patientId: patientId,
              patientName: `${response.data.dataModel.lastName}, ${response.data.dataModel.firstName}`,
              payerId: firstPlan?.payerId || primaryPlan.insurancePlanID,
              subscriberId: primaryPlan.subscriberId,
              patientDob: ptModel.dob,
              patientPlanId: patientPlanId, 
              subscriberFirstName: ptModel.firstName,
              subscriberLastName: ptModel.lastName,
              patientRelation: primaryPlan.relationShip,
              insurancePayerTypeId: firstPlan?.insurancePayerTypeId,
              allPlans: plans,
            });
            setShowEligibilityPopup(true);
          } catch (error) {
            ErrorHandlingMessage(error);
            if (pendingPatientToOpen) {
              dispatch(addPatient(pendingPatientToOpen));
              setPendingPatientToOpen(null);
            } else {
              dispatch(addPatient(obj));
            }
            setShowSuccessScreen(true);
          }
        } else {
          dispatch(addPatient(obj));
          setShowSuccessScreen(true);
        }
      } catch (error) {
        setLoading(false);
        if (error.response?.data?.dataModel?.doOverrideDuplicateEmail) {
          setDuplicateEmailConfirmation(true);
        } else if (error.response?.data?.dataModel?.doOverrideDuplicateAddress) {
          setDuplicateAddressConfirmation(true);
        } else if (error.response?.data?.dataModel?.doOverrideDuplicatePhoneNumber) {
          setDuplicatePhoneConfirmation(true);
        } else {
          ErrorHandlingMessage(error);
        }
      }
    }
  };

  const handleRegisterAnother = () => {
    setPTModel(getInitialPatientModel());
    setValidationModel(getInitialValidationModel());
    setValidationByPlan({});
    setInsuranceCardFront(null);
    setInsuranceCardBack(null);
    setInsurancePlans([
      {
        coverage: "P",
        expanded: true,
        sectionsOpen: {
          cards: true,
          plan: true,
          group: true,
          costShare: true,
          subscriber: true,
        },
        cardFront: null,
        cardBack: null,
      },
    ]);
    setShowOnlyRequired(false);
    setSectionsOpen({
      identification: true,
      address: true,
      contact: true,
      guarantor: false,
    });
    setInsuranceExpanded(true);
    setInsuranceSectionsOpen({
      cards: true,
      plan: true,
      group: true,
      costShare: true,
      subscriber: true,
    });
    setShowSuccessScreen(false);
    setDuplicateEmailConfirmation(false);
    setDuplicatePhoneConfirmation(false);
    setDuplicateAddressConfirmation(false);
    setPendingCheckEligibility(false);
    setPendingPatientToOpen(null);
  };

  const handleClosePopup = () => {
    onClose(true);
  };

  const handleSaveAndCheckEligibility = () => {
    handleSave(true);
  };

  const handleSaveOnly = () => {
    handleSave(false);
  };

  const handleToggleShowOnlyRequired = () => {
    setShowOnlyRequired(!showOnlyRequired);
  };

  const handleSelfPayChange = (e) => {
    const newValue = !ptModel.isSelfPay;
    if (newValue) {
      setInsuranceCardFront(null);
      setInsuranceCardBack(null);
      setInsuranceExpanded(false);
      setInsuranceSectionsOpen({
        cards: true,
        plan: true,
        group: true,
        costShare: true,
        subscriber: true,
      });
      // Remove secondary/tertiary sections so user doesn't see blank sections when on Self Pay
      setInsurancePlans([
        {
          coverage: "P",
          expanded: true,
          sectionsOpen: {
            cards: true,
            plan: true,
            group: true,
            costShare: true,
            subscriber: true,
          },
          cardFront: null,
          cardBack: null,
        },
      ]);
      setPTModel((prev) => ({
        ...prev,
        isSelfPay: newValue,
        // Reset plans back to a single default Primary plan
        patientPlan: [
          {
            ...getDefaultPlanModel(),
            coverage: "P",
          },
        ],
      }));
      setValidationByPlan({});
    } else {
      // When Self Pay is disabled, open the insurance section
      setInsuranceExpanded(true);
      setPTModel((prev) => ({
        ...prev,
        isSelfPay: newValue,
      }));
    }
  };

  const handleToggleInsuranceExpanded = () => {
    // Prevent expansion when Self Pay is enabled
    if (ptModel.isSelfPay) {
      return;
    }
    setInsuranceExpanded(!insuranceExpanded);
  };

  const handleEligibilityPopupClose = () => {
    setShowEligibilityPopup(false);
    if (pendingPatientToOpen) {
      dispatch(addPatient(pendingPatientToOpen));
      setPendingPatientToOpen(null);
    }
    setShowSuccessScreen(true);
  };

  const handleEligibilityRun = (response, model) => {
    setShowEligibilityPopup(false);
    const result = response?.data?.dataModel ?? null;
    setInitialEligibilityResult(result);
    setShowPatientEligibilityPopup(true);
    if (pendingPatientToOpen) {
      dispatch(addPatient(pendingPatientToOpen));
      setPendingPatientToOpen(null);
    }
  };

  const handlePatientEligibilityPopupClose = () => {
    setShowPatientEligibilityPopup(false);
    setInitialEligibilityResult(null);
    setShowSuccessScreen(true);
  };

  useEffect(() => {
        const primaryPlan = ptModel.patientPlan?.[0];
    const hasInsuranceCards = !!(insuranceCardFront || insuranceCardBack);
    const hasInsuranceFields =
      primaryPlan &&
      !!(
        (primaryPlan.insurancePlanID && primaryPlan.insurancePlanID > 0) ||
        (primaryPlan.insurancePayerName &&
          primaryPlan.insurancePayerName.trim() !== "") ||
        (primaryPlan.subscriberId &&
          primaryPlan.subscriberId.trim() !== "") ||
        (primaryPlan.relationShip &&
          primaryPlan.relationShip.trim() !== "" &&
          primaryPlan.relationShip !== "18")
      );
    const insuranceStarted = hasInsuranceCards || hasInsuranceFields;

    if (!insuranceStarted) {
      setValidationModel((prev) => ({
        ...prev,
        address1Error: null,
        zipCodeError: null,
      }));
      setValidationByPlan({});
    }

    if (primaryPlan?.relationShip && primaryPlan.relationShip !== "18") {
      setInsuranceSectionsOpen((prev) => ({
        ...prev,
        subscriber: true,
      }));
    }
  }, [ptModel.patientPlan, insuranceCardFront, insuranceCardBack]);

 
  return (
    <ReactPortal wrapperId={UNIQUE_ID}>
      {loading && <FullPageLoader allowFullScreen />}
      {confirmations.map(
        (c, i) =>
          c.condition && (
            <ConfirmationPopup
              key={i}
              buttonTitle="Yes"
              cancelButtonTitle="Cancel"
              heading="Confirmation"
              onCancel={c.onCancel}
              onConfirm={() => handleSave(pendingCheckEligibility)}
              subHeading={c.message}
            />
          )
      )}
      <div
        data-te-modal-init
        className="fixed inset-0 z-10 flex items-center justify-center bg-gray-500 bg-opacity-75"
        id="exampleModalScrollable"
        tabIndex={-1}
        aria-labelledby="exampleModalScrollableLabel"
        aria-modal="true"
        data-te-open="true"
      >
        <CustomDraggable
          disableDraggable={disableDraggable}
          handle={`#${UNIQUE_ID}`}
        >
          <motion.div
            data-te-modal-dialog-ref
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className={`relative min-[576px]:mx-auto min-[576px]:mt-2 flex flex-col overflow-hidden rounded-xl bg-white shadow-xl ${
              showSuccessScreen 
                ? "w-[400px] max-w-[400px]" 
                : "w-[900px] min-[576px]:max-w-[500px] min-[992px]:max-w-[800px] min-[1200px]:max-w-[1140px]"
            }`}
          >
            <div className="p-6 pt-0 ">
              {showSuccessScreen ? (
                <SuccessScreen
                  onRegisterAnother={handleRegisterAnother}
                  onClose={handleClosePopup}
                />
              ) : (
                <>
                  <PopupHeader uniqueId={UNIQUE_ID} heading="Patient Registration"  onClose={handleClosePopup} description="Please complete all required information to register a new patient"/>
                  <div className="overflow-y-auto max-h-[70vh] px-2">
                    <BasicInformation
                      ptModel={ptModel}
                      validationModel={validationModel}
                      showOnlyRequired={showOnlyRequired}
                      sectionsOpen={sectionsOpen}
                      onToggleSection={toggleSection}
                      onShowOnlyRequiredChange={handleToggleShowOnlyRequired}
                      handleChange={handleChange}
                      onPasteEmail={onPasteEmail}
                      onPasteZip={onPasteZip}
                      handleGuarantorZipChange={handleGuarantorZipChange}
                      onToggleChange={handleCommunicationPreferenceChange}
                      hasInsuranceDataStarted={hasInsuranceDataStarted}
                    />

                    {/* Primary Insurance Plan */}
                    <InsuranceInformation
                      ptModel={ptModel}
                      validationModel={
                        validationByPlan[0] ?? getInitialInsuranceValidationModel()
                      }
                      insuranceExpanded={insuranceExpanded}
                      insuranceSectionsOpen={insuranceSectionsOpen}
                      insuranceCardFront={insuranceCardFront}
                      insuranceCardBack={insuranceCardBack}
                      onInsuranceExpandedChange={handleToggleInsuranceExpanded}
                      onToggleInsuranceSection={toggleInsuranceSection}
                      handleChange={handleChange}
                      handleCardUpload={(e, cardType) => handleCardUpload(e, cardType, 0)}
                      handleCardRemove={(cardType) => handleCardRemove(cardType, 0)}
                      hasInsuranceDataStarted={hasInsuranceDataStarted}
                      onPasteZip={handleSubscriberZipChange}
                      isSelfPay={ptModel.isSelfPay}
                      onSelfPayChange={handleSelfPayChange}
                      planIndex={0}
                      coverageType="P"
                      showRemoveButton={false}
                      onSameAsPatientAddressChange={handleSubscriberSameAsPatientAddress}
                    />

                    {/* Additional Insurance Plans - hide when Self Pay so user doesn't see blank sections */}
                    {!ptModel.isSelfPay &&
                      insurancePlans.slice(1).map((plan, index) => {
                        const planIndex = index + 1;
                        return (
                          <InsuranceInformation
                            key={planIndex}
                            ptModel={ptModel}
                            validationModel={
                              validationByPlan[planIndex] ??
                              getInitialInsuranceValidationModel()
                            }
                            insuranceExpanded={plan.expanded}
                            insuranceSectionsOpen={plan.sectionsOpen}
                            insuranceCardFront={plan.cardFront}
                            insuranceCardBack={plan.cardBack}
                            onInsuranceExpandedChange={() => handleInsuranceExpandedChange(planIndex)}
                            onToggleInsuranceSection={(section) => handleToggleInsuranceSection(section, planIndex)}
                            handleChange={(e) => handlePlanChange(e, planIndex)}
                            handleCardUpload={(e, cardType) => handleCardUpload(e, cardType, planIndex)}
                            handleCardRemove={(cardType) => handleCardRemove(cardType, planIndex)}
                            hasInsuranceDataStarted={hasInsuranceDataStarted}
                            onPasteZip={handleSubscriberZipChange}
                            isSelfPay={ptModel.isSelfPay}
                            onSelfPayChange={handleSelfPayChange}
                            planIndex={planIndex}
                            coverageType={plan.coverage}
                            onRemovePlan={handleRemoveInsurancePlan}
                            showRemoveButton={true}
                            onSameAsPatientAddressChange={handleSubscriberSameAsPatientAddress}
                          />
                        );
                      })}

                    {/* Add Policy Button */}
                    {!ptModel.isSelfPay && insurancePlans.length < 4 && (
                      <div className="mb-6">
                        <CustomButtonNew
                          onClick={handleAddInsurancePlan}
                          label="Add Policy"
                          variant="outline"
                          prefixIcon={<PlusIcon size={15} />}
                        />
                      </div>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center justify-end gap-2 mt-5">
                    <div
                      data-tooltip-id="eligibility-button-tooltip"
                      data-tooltip-content={!isInsurancePlanValid() ? "Payer information is required in order to check for the Eligibility. Please fill all required Insurance Information." : ""}
                    >
                      <CustomButtonNew 
                        onClick={handleSaveAndCheckEligibility} 
                        label="Save and Check Eligibility" 
                        disabled={loading || !isInsurancePlanValid() || ptModel.isSelfPay}
                        variant="outline"
                      />
                    </div>
                    <CustomButtonNew 
                      onClick={handleSaveOnly} 
                      label="Save" 
                      disabled={loading}
                    />
                  </div>
                </>
              )}
            </div>
          </motion.div>
        </CustomDraggable>
      </div>
      {/* Tooltips for Communication Preferences */}
      <Tooltip id="email-pref-tooltip" style={{ zIndex: 10, maxWidth: 300 }} />
      <Tooltip id="text-pref-tooltip" style={{ zIndex: 10, maxWidth: 300 }} />
      <Tooltip id="mail-pref-tooltip" style={{ zIndex: 10, maxWidth: 300 }} />
      {/* Tooltip for Save and Check Eligibility button */}
      <Tooltip id="eligibility-button-tooltip" style={{ zIndex: 10, maxWidth: 300 }} />
      {/* Eligibility Popup */}
      {showEligibilityPopup && (
        <EligibiltyPopup
          onClose={handleEligibilityPopupClose}
          onEligibilityRun={handleEligibilityRun}
          disableDraggable={disableDraggable}
          patientId={eligibilityData.patientId}
          patientName={eligibilityData.patientName}
          payerId={eligibilityData.payerId}
          dateOfService={eligibilityData.dateOfService}
          cptCode={eligibilityData.cptCode}
          serviceType={eligibilityData.serviceType}
          subscriberId={eligibilityData.subscriberId}
          patientDob={eligibilityData.patientDob}
          patientPlanId={eligibilityData.patientPlanId}
          subscriberFirstName={eligibilityData.subscriberFirstName}
          subscriberLastName={eligibilityData.subscriberLastName}
          patientRelation={eligibilityData.patientRelation}
          insurancePayerTypeId={eligibilityData.insurancePayerTypeId}
          allPlans={eligibilityData.allPlans}
        />
      )}
      {showPatientEligibilityPopup && (
        <PatientEligibilityPopup
          patientId={eligibilityData.patientId}
          patientName={eligibilityData.patientName}
          onClose={handlePatientEligibilityPopupClose}
          initialEligibilityResult={initialEligibilityResult}
        />
      )}
    </ReactPortal>
  );
};

export default NewPatientPopup;

NewPatientPopup.propTypes = {
  onClose: PropTypes.func.isRequired,
  disableDraggable: PropTypes.bool,
};
